import { useState } from "react";
import * as Sentry from "@sentry/react";
import { useNotifications } from "@via/hooks/useNotifications";
import { useSendTestEmail } from "@via/pages/account/admin/admin.api.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import { useForm } from "react-hook-form";
import { healthCheckOptions } from "@via/utils/form-validators.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component";
import FormTextAreaComponent from "@via/components/inputs/form-textarea-component";
import { SendTestEmailRequestBody } from "common";
import AuditHealthCard from "@via/pages/account/admin/health-check/audit-health-card.tsx";

const HealthCheck = () => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const [shouldCrash, setShouldCrash] = useState(false);
  const sendTestEmail = useSendTestEmail();

  // --- Option A: Directly capture to Sentry (no boundary needed)
  const sendTestErrorToSentry = () => {
    const timestamp = new Date().toISOString();

    setNotification({
      id: `sentry-test-${timestamp}`,
      message: "Test error sent to Sentry",
      details: `Timestamp: ${timestamp}. Check the Sentry dashboard.`,
      variant: NOTIFICATION.SUCCESS,
    });

    Sentry.captureException(
      new Error(`Simulated Sentry error at ${timestamp}`),
      {
        level: "error",
        tags: { source: "health-check", kind: "manual-capture" },
      }
    );
  };

  // --- Option B: Trigger a render-time error to hit ErrorBoundary
  const triggerRenderCrash = () => {
    const timestamp = new Date().toISOString();

    setNotification({
      id: `sentry-render-crash-${timestamp}`,
      message: "Render crash triggered (ErrorBoundary test)",
      details: `Timestamp: ${timestamp}. This will remount via the boundary. Check the Sentry dashboard.`,
      variant: NOTIFICATION.INFO,
    });

    // Give the notification a tick to render, then crash in render phase
    setTimeout(() => setShouldCrash(true), 0);
  };

  if (shouldCrash) {
    const timestamp = new Date().toISOString();
    throw new Error(`Forced render error at ${timestamp} (HealthCheck)`);
  }

  const { control, handleSubmit } = useForm<SendTestEmailRequestBody>({
    mode: "onSubmit",
    defaultValues: {
      toEmail: "",
      subject: "",
      body: "",
    },
  });

  const onSendTestEmail = handleSubmit(values => {
    sendTestEmail.mutate({
      toEmail: values.toEmail.trim(),
      ...(values.subject?.trim() && { subject: values.subject.trim() }),
      ...(values.body?.trim() && { body: values.body.trim() }),
    });
  });

  return (
    <div className="p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg">
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap gap-3">
          <button
            onClick={sendTestErrorToSentry}
            className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            Send Test Error to Sentry
          </button>

          <button
            onClick={triggerRenderCrash}
            className="inline-flex justify-center rounded-md bg-rose-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-600">
            Trigger Render Error (Boundary)
          </button>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <AuditHealthCard />
        </div>

        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            Send test email notification
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Send a test email to verify that pipeline run notifications (and
            other platform emails) are delivered correctly, without running a
            pipeline.
          </p>
          <div className="flex flex-col gap-3 max-w-md">
            <FormInputComponent
              control={control}
              name="toEmail"
              label="Recipient email"
              placeholder="e.g. admin@example.com"
              maxLength={254}
              title="Max 254 characters; local-part (before @) max 64, domain (after @) max 255"
              validations={healthCheckOptions.toEmail}
              showRequiredField
            />
            <FormInputComponent
              control={control}
              name="subject"
              label="Subject (optional)"
              placeholder="Test email from Foundry platform"
            />
            <FormTextAreaComponent
              control={control}
              name="body"
              label="Body (optional)"
              placeholder="This is a test email to confirm that email notifications are working."
              rows={3}
            />
            <button
              type="button"
              onClick={onSendTestEmail}
              disabled={sendTestEmail.isPending}
              className="inline-flex justify-center rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed">
              <Spinner loading={sendTestEmail.isPending}>
                Send Test Email
              </Spinner>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthCheck;
